import _ from "lodash";
import React, { useMemo, useContext, useState, useEffect, useRef } from "react";
import classes from "./SearchAndAddDropdown.scss";
import { WithSearchDropdownQueryData } from "AppComponents/CollaboratorV2/modules/HOC";
import { Avatar, Button, SelectDropdown } from "@toddle-edu/ds-web";
import { useI18n, useMount } from "Hooks";
import PaginationLoader from "UIComponents/PaginationLoader";
import { getOptionItemDetails } from "AppComponents/CollaboratorV2/modules/Utils";
import { CollaboratorV2Context } from "AppComponents/CollaboratorV2/modules/Modules";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";
import StudentAlerts from "AppComponents/StudentAlerts";
import classNames from "classnames";

const styles = {
  loadingStyle: {
    width: 28,
    height: 28,
  },
};

const AddExternalCollaborator = ({ onClickAdd, testId }) => {
  const { t } = useI18n(["common", "project"]);

  return (
    <div
      className={classNames(classes.addExternalCollaboratorOption, "text-body")}
      onClick={onClickAdd}
    >
      <AddCircleOutlined variant="subtle" size="xx-small" />
      <span className={classes.textPrimary}>{`${t(
        "common:no_match_found"
      )} - `}</span>
      <Button
        size="medium"
        variant="progressive"
        type="inline"
        dsVersion="2.0"
        testId={`${testId}-inviteSupervisor`}
      >
        {t("project:invite_supervisor")}
      </Button>
    </div>
  );
};

const SearchAndAddDropdown = props => {
  const {
    isLoading,
    isData,
    studentList,
    staffList,
    onAddOptions,
    selectedOptions,
    updateDoneButtonDisabled,
    testId,
  } = props;

  const contextValue = useContext(CollaboratorV2Context);

  const selectDropdownRef = useRef(null);

  const {
    showCurriculum,
    isDropdownFocusedOnMount,
    updateAddEditUserModalDetails,
    isAddingNewCollaboratorEnabled,
  } = contextValue;

  useMount(() => {
    if (
      isDropdownFocusedOnMount &&
      _.size(selectedOptions) == 0 &&
      selectDropdownRef.current
    ) {
      // click is not working, have pinged satish to know how to achieve the behaviour
      selectDropdownRef.current.focus();
    }
  });

  const [searchAddedOptions, setSearchAddedOptions] = useState([]); // state for pills created while search and add

  const updateSearchAddedOptions = value => {
    setSearchAddedOptions(value);
  };

  const onAddExternalCollaborator = value => {
    setSearchAddedOptions(prevSearchAddedOptions => {
      return [...prevSearchAddedOptions, value];
    });
  };

  const { t } = useI18n(["common", "project"]);

  const isSearchAddedOptionsEmpty = _.size(searchAddedOptions) == 0;

  useEffect(() => {
    if (updateDoneButtonDisabled) {
      updateDoneButtonDisabled(!isSearchAddedOptionsEmpty);
    }
  }, [isSearchAddedOptionsEmpty, updateDoneButtonDisabled]);

  const collaboratorList = studentList ? studentList : staffList;
  const collaboratorType = studentList ? "STUDENT" : "STAFF";

  const dropdownOptions = useMemo(() => {
    const totalOptions = _.map(collaboratorList, item => {
      const {
        name,
        itemCurricullamPrograms,
        id,
        profileImage,
        isExternal,
        email,
        studentAlertsConfig,
      } = getOptionItemDetails({
        item,
        itemType: collaboratorType,
      });

      const subtitle = `${
        isExternal ? email : showCurriculum ? itemCurricullamPrograms : ""
      }`;

      return {
        ...item,
        value: id,
        label: name,
        labelSuffix: (
          <div className={classes.optionLabel}>
            <StudentAlerts
              studentId={studentAlertsConfig?.studentId}
              alertTypes={studentAlertsConfig?.alertTypes}
              shouldAutoFocusPopover={false}
            />
          </div>
        ),
        subtitle,
        icon: (
          <Avatar
            name={name}
            size={"small"}
            id={id}
            src={profileImage}
            dsVersion="2.0"
            shape="square"
          />
        ),
      };
    });

    // currently not showing the added options in DD list, the tick is not avl and have to confirm with DS
    const filteredOptions = _.differenceBy(totalOptions, selectedOptions, "id");
    return filteredOptions;
  }, [collaboratorList]);

  const [filteredStudentOptions, setFilteredStudentOptions] = useState(
    dropdownOptions
  );

  useEffect(() => {
    setFilteredStudentOptions(dropdownOptions);
  }, [collaboratorList]);

  const handleOnAddClick = () => {
    const updatedValue = _.uniqBy(
      _.concat(searchAddedOptions, selectedOptions),
      "id"
    );

    onAddOptions(updatedValue);
    updateSearchAddedOptions([]);
  };

  const handleStudentSearchTextChange = searchText => {
    if (!searchText) {
      return setFilteredStudentOptions(dropdownOptions);
    }

    const matchingStudentIds = _.chain(collaboratorList)
      .filter(student => {
        const { name } = getOptionItemDetails({
          item: student,
          itemType: collaboratorType,
        });
        return _.includes(_.toLower(name), _.toLower(searchText));
      })
      .map("id")
      .value();

    const filteredStudentList = _.filter(dropdownOptions, option =>
      _.includes(matchingStudentIds, option?.id)
    );

    setFilteredStudentOptions(filteredStudentList);
  };

  const handleStudentSearchVisibleChange = () => {
    setFilteredStudentOptions(dropdownOptions);
  };

  return (
    <div className={classes.container}>
      <div className={classes.selectDropdownContainer}>
        <SelectDropdown
          onMenuClose={handleStudentSearchVisibleChange}
          placement={"bottomCenter"}
          ref={selectDropdownRef}
          size="medium"
          value={searchAddedOptions}
          options={filteredStudentOptions}
          onChange={updateSearchAddedOptions}
          isSearchEnabled={true}
          isMulti={true}
          allOptionPrefix={
            <Avatar
              size={"small"}
              iconType={"group"}
              dsVersion="2.0"
              shape="square"
            />
          }
          onSearchTextChange={handleStudentSearchTextChange}
          staticOption={
            isAddingNewCollaboratorEnabled ? (
              <AddExternalCollaborator
                onClickAdd={() => {
                  updateAddEditUserModalDetails({
                    displayModal: true,
                    mode: "CREATE",
                    onAddExternalCollaborator: onAddExternalCollaborator,
                  });
                }}
                testId={testId}
              />
            ) : null
          }
          placeholder={
            isAddingNewCollaboratorEnabled
              ? t("common:type_a_name_or_email")
              : t("common:type_a_name")
          }
          loader={
            !isData && isLoading ? (
              <PaginationLoader loadingStyle={styles.loadingStyle} />
            ) : null
          }
          noOptionsText={t("common:noResultHeading")}
          testId={testId}
        />
      </div>
      <Button
        variant="progressive"
        size="medium"
        onClick={handleOnAddClick}
        disabled={isSearchAddedOptionsEmpty}
        testId={`${testId}-add`}
      >
        {t("common:add")}
      </Button>
    </div>
  );
};

export default WithSearchDropdownQueryData(SearchAndAddDropdown);
